import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

// SERVICES
import { RequestsService } from 'src/app/core/services/http/requests.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { PayBillsService } from '../paybills.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ImageValidatorHelperService } from 'src/app/services/image-validator-helper.service';

// TYPES
import { ImageFile } from 'src/app/core/model/common/ImageFile.interface';

// CONSTANTS
import { Urls } from 'src/app/lib/urls';
import { SKU_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { BILLER_TABLE_HEADERS, BILLER_TABLE_SUB_HEADERS } from 'src/app/lib/constants/paybills.constants';
import { BILLER_CATEGORY_OPTIONS, FIELD_FORMAT_OPTIONS } from 'src/app/lib/constants/dropdown-options.constants';
import { ChangeDetectionService } from 'src/app/services/change-detection.service';
import { RbacService } from 'src/app/services/rbac.service';
import { Permission } from 'src/app/lib/rbac.config';

@Component({
  selector: 'app-sku-pay-bills',
  templateUrl: './sku-pay-bills.component.html',
  styleUrls: ['./sku-pay-bills.component.scss'],
})
export class SkuPayBillsComponent implements OnInit {
  public payBillsForm: UntypedFormGroup;
  paybills_lists: any;
  isLoading: boolean;

  // tabs
  tabs = SKU_TAB_OPTIONS;

  // to track user interaction
  originalValues: any = {};

  // Table headers
  tableHeader = BILLER_TABLE_HEADERS;
  tableSubHeader = BILLER_TABLE_SUB_HEADERS;

  // Dropdown options
  billerCategoryOptions = BILLER_CATEGORY_OPTIONS;
  fieldFormatOptions = FIELD_FORMAT_OPTIONS;

  // add biller
  isDisplay = true;

  // Biller Provider
  billerProvider_text: any;
  billerProvider: any;

  // Biller Category
  billerCategory_text: any;
  billerCategory: any;

  // First Field Format
  firstFieldFormat_text: any;
  firstFieldFormat: any;

  // Second Field Format
  secondFieldFormat_text: any;
  secondFieldFormat: any;

  // service fee
  serviceFeePercentage: any;
  serviceFeeFixed: any;

  // image 
  imageFile: ImageFile;
  page = 1;
  size = 10;
  total_pages: any;
  current_page = 1;
  pages_array = [];
  total: any;

  // search
  searchKey: any = null;
  searchForm: UntypedFormGroup;
  search_keyword = '';

  // limit
  limitKey: any = null;
  limitForm: UntypedFormGroup;

  // sorting
  sort_column = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  constructor(
    private fb: UntypedFormBuilder,
    private request: RequestsService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private imageValidatorService: ImageValidatorHelperService,
    private changeDetectionService: ChangeDetectionService,
    private rbac: RbacService,
    public debounce: DebounceService,
    public popUpDialog: PopUpDialogService,
    public paginationHelper: PaginationHelperService,
    public payBillsService: PayBillsService,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.tabs = SKU_TAB_OPTIONS.filter(tab => !tab['permission'] || this.rbac.hasPermission(tab['permission'] as Permission));
    this.isLoading = true;
    this.get_payBills_list();
    this.storeOriginalValues();
  }

  initializeForm() {
    this.payBillsForm = this.fb.group({
      billerName: [
        '', 
        [
          Validators.required,
          Validators.maxLength(50),
        ]
      ],
      billerDescription: [
        '', 
        [
          Validators.required,
          Validators.maxLength(50),
        ]
      ],
      firstFieldName: ['', Validators.required],
      firstFieldWidth: ['', Validators.required],
      secondFieldName: ['', Validators.required],
      secondFieldWidth: ['', Validators.required],
      convenienceFee: ['', Validators.required],
      serviceFeePercentage: [
        '', 
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
        ],
      ],
      serviceFeeFixed: ['', Validators.required],
      image: [null]
    });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      this.get_payBills_list();
    }, 500);

    this.searchForm = this.fb.group({
      search: [''],
    });

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      this.get_payBills_list();
    }, 500);

    this.limitForm = this.fb.group({
      limit: [''],
    });
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC') {
    this.spinner.show().catch(err => console.error('Spinner show sort error:', err));

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;
    
    if (this.paybills_lists) {
      this.get_payBills_list();
    }

    this.spinner.hide().catch(err => console.error('Spinner hide sort error:', err));
  }

  async store() {
    if (!this.billerProvider) {
      this.billerProvider = 1;
    }

    // Validate Biller Name
    const billerNameControl = this.payBillsForm.controls.billerName;
    if (!billerNameControl.value) {
      this.toastr.error('Biller Name is required!', 'Validation Error');
      return;
    }
    if (billerNameControl.hasError('maxlength')) {
      this.toastr.error('Biller Name must not exceed 50 characters!', 'Validation Error');
      return;
    }
    if (billerNameControl.hasError('pattern')) {
      this.toastr.error('Biller Name must contain only letters and spaces', 'Validation Error');
      return;
    }

    // Validate Biller Description
    const billerDescControl = this.payBillsForm.controls.billerDescription;
    if (!billerDescControl.value) {
      this.toastr.error('Biller Description is required!', 'Validation Error');
      return;
    }
    if (billerDescControl.hasError('pattern')) {
      this.toastr.error('Biller Description must only contains letters, spaces, and only "&" special character.', 'Validation Error');
      return;
    }

    // Validate Service Fee Percentage (0-100) only
    const serviceFeeCtrl = this.payBillsForm.controls.serviceFeePercentage;
    if (serviceFeeCtrl.hasError('min') || serviceFeeCtrl.hasError('max')) {
      this.toastr.error('Service Fee Percentage must be between 0 and 100', 'Validation Error');
      return;
    }

    // Validate other required fields
    if (!this.payBillsForm.controls.firstFieldName.value) {
      this.toastr.error('First Field Name is required!', 'Validation Error');
      return;
    }

    if (!this.payBillsForm.controls.firstFieldWidth.value) {
      this.toastr.error('First Field Max Length is required!', 'Validation Error');
      return;
    }

    if (!this.payBillsForm.controls.secondFieldName.value) {
      this.toastr.error('Second Field Name is required!', 'Validation Error');
      return;
    }

    if (!this.payBillsForm.controls.secondFieldWidth.value) {
      this.toastr.error('Second Field Max Length is required!', 'Validation Error');
      return;
    }

    const convenienceFee = this.payBillsForm.controls.convenienceFee.value;
    if (convenienceFee === null || convenienceFee === undefined || convenienceFee === '') {
      this.toastr.error('Convenience Fee is required!', 'Validation Error');
      return;
    }

    const serviceFeeFixedValue = this.payBillsForm.controls.serviceFeeFixed.value;
    if (serviceFeeFixedValue === null || serviceFeeFixedValue === undefined || serviceFeeFixedValue === '') {
      this.toastr.error('Service Fee Fixed is required!', 'Validation Error');
      return;
    }

    const serviceFeePercentageValue = this.payBillsForm.controls.serviceFeePercentage.value;
    if (serviceFeePercentageValue === null || serviceFeePercentageValue === undefined || serviceFeePercentageValue === '') {
      this.toastr.error('Service Fee Percentage is required!', 'Validation Error');
      return;
    }

    if (!this.billerCategory) {
      this.toastr.error('Biller Category is required!', 'Validation Error');
      return;
    }

    if (!this.firstFieldFormat) {
      this.toastr.error('First Field Format is required!', 'Validation Error');
      return;
    }

    if (!this.secondFieldFormat) {
      this.toastr.error('Second Field Format is required!', 'Validation Error');
      return;
    }


    let rawPercentage = Number(this.payBillsForm.controls.serviceFeePercentage.value);

    let finalPercentage = 0;

    if (!rawPercentage) {
      finalPercentage = 0;
    } else if (rawPercentage >= 1) {
      // Input like 1 → 1%
      finalPercentage = Number((rawPercentage / 100).toFixed(6));
    } else {
      // Input like 0.01 → already decimal
      finalPercentage = Number(rawPercentage.toFixed(6));
    }
    
    const formData = {
      billerName: this.payBillsForm.controls.billerName.value,
      billerDescription: this.payBillsForm.controls.billerDescription.value,
      firstFieldName: this.payBillsForm.controls.firstFieldName.value,
      firstFieldWidth: this.payBillsForm.controls.firstFieldWidth.value,
      secondFieldName: this.payBillsForm.controls.secondFieldName.value,
      secondFieldWidth: this.payBillsForm.controls.secondFieldWidth.value,
      convenienceFee: this.payBillsForm.controls.convenienceFee.value,
      billerCategory: this.billerCategory,
      firstFieldFormat: this.firstFieldFormat,
      secondFieldFormat: this.secondFieldFormat,
      serviceFeePercentage: finalPercentage,
      serviceFeeFixed: this.payBillsForm.controls.serviceFeeFixed.value,
      platform: this.billerProvider
    };

    const response = await this.payBillsService.performStore(formData, this.imageFile);

    if (response.success) {
      this.get_payBills_list();
      this.resetForm();
    }
  }

  resetForm() {
    this.payBillsForm.reset();
    this.imageFile = null;
    this.billerProvider_text = null;
    this.billerProvider = null;
    this.firstFieldFormat_text = null;
    this.firstFieldFormat = null;
    this.secondFieldFormat_text = null;
    this.secondFieldFormat = null;
    this.billerCategory_text = null;
    this.billerCategory = null;
    this.storeOriginalValues();
  }

  async get_payBills_list() {
  this.spinner.show();

  try {
    const response = await this.payBillsService.getBillersList(
      this.current_page,
      this.size,
      this.search_keyword,
      this.sort_column,
      this.sort_type
    );

    if (response.status === 200) {
     this.paybills_lists = response.result.data.map(biller => {
        let percentage = Number(biller.serviceFeePercentage) || 0;

        if (percentage > 1) {
          // Already in percent (e.g., 9 = 9%)
          percentage = Number(percentage.toFixed(2));
        } else {
          // Decimal (e.g., 0.09 = 9%)
          percentage = Number((percentage * 100).toFixed(2));
        }

        return {
          ...biller,
          serviceFeePercentage: percentage
        };
      });

      this.isLoading = false;

      this.pages_array = [];
      for (let i = this.current_page; i <= response.result.totalPages; i++) {
        this.pages_array.push(i);
      }

      this.total_pages = parseInt(response.result.totalPages, 10);
      this.current_page = response.result.currentPage;
      this.total = parseInt(response.result.totalItems, 10);
    }
    } catch (error) {
      this.toastr.error(
        `Please refresh or contact the developer if the error still occurs`,
        'Something went wrong'
      );
    } finally {
      setTimeout(() => {
        this.spinner.hide();
      }, 500);
    }
  }

  setBillerProvider(text, value) {
    this.billerProvider = value;
    this.billerProvider_text = text;
  }

  setBillerCategory(label: string, value: string) {
    this.billerCategory = value;
    this.billerCategory_text = label;
  }

  setFirstFieldFormat(label: string, value: string) {
    this.firstFieldFormat = value;
    this.firstFieldFormat_text = label;
  }

  setSecondFieldFormat(label: string, value: string) {
    this.secondFieldFormat = value;
    this.secondFieldFormat_text = label;
  }

  setPage(num) {
    this.current_page = num;
    this.get_payBills_list();
  }

  nextPage() {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.get_payBills_list();
    }
  }

  prevPage() {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_payBills_list();
    }
  }

  toggleDisplay() {
    this.isDisplay = !this.isDisplay;
  }

  keyDownFunction(event) {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList() {
    this.current_page = 1;
    this.search_keyword = this.searchForm.value.search;
    this.get_payBills_list();
  }

  disable(id: number, value: number) {
    this.toggleStatus(id, value, 'disable');
  }

  enable(id: number, value: number) {
    this.toggleStatus(id, value, 'enable');
  }

  toggleStatus(id: number, value: number, action: 'enable' | 'disable'): void {
    const messages = {
      enable: 'Are you sure you want to enable this Biller?',
      disable: 'Are you sure you want to disable this Biller?',
    };

    this.popUpDialog.openStatusActionDialog(
      messages[action],
      action.toUpperCase(),
      'CANCEL',
      async () => {
        const response = await this.payBillsService.performStatusChange(id, value);
        if (response.success) {
          this.get_payBills_list();
        }
      },
      () => console.log(`${action} cancelled`)
    );
  }

  openSite(imageUrl) {
    window.open(imageUrl, '_blank');
  }

  viewUploadedImage() {
    if (this.imageFile && this.imageFile.link) {
      this.popUpDialog.openImageViewDialog(this.imageFile.link, this.imageFile.name);
    }
  }

  storeOriginalValues() {
    const valuesToStore = {
      billerName: this.payBillsForm.controls.billerName.value || '',
      billerDescription: this.payBillsForm.controls.billerDescription.value || '',
      firstFieldName: this.payBillsForm.controls.firstFieldName.value || '',
      secondFieldName: this.payBillsForm.controls.secondFieldName.value || '',
      firstFieldWidth: this.payBillsForm.controls.firstFieldWidth.value || '',
      secondFieldWidth: this.payBillsForm.controls.secondFieldWidth.value || '',
      convenienceFee: this.payBillsForm.controls.convenienceFee.value || '',
      serviceFeePercentage: this.payBillsForm.controls.serviceFeePercentage.value || '',
      serviceFeeFixed: this.payBillsForm.controls.serviceFeeFixed.value || '',
      billerCategory: this.billerCategory || '',
      firstFieldFormat: this.firstFieldFormat || '',
      secondFieldFormat: this.secondFieldFormat || '',
      imageFile: null
    };

    this.originalValues = this.changeDetectionService.cloneValues(valuesToStore);
  }

  getCurrentValues(): any {
    return {
      billerName: this.payBillsForm.controls.billerName.value || '',
      billerDescription: this.payBillsForm.controls.billerDescription.value || '',
      firstFieldName: this.payBillsForm.controls.firstFieldName.value || '',
      secondFieldName: this.payBillsForm.controls.secondFieldName.value || '',
      firstFieldWidth: this.payBillsForm.controls.firstFieldWidth.value || '',
      secondFieldWidth: this.payBillsForm.controls.secondFieldWidth.value || '',
      convenienceFee: this.payBillsForm.controls.convenienceFee.value || '',
      serviceFeePercentage: this.payBillsForm.controls.serviceFeePercentage.value || '',
      serviceFeeFixed: this.payBillsForm.controls.serviceFeeFixed.value || '',
      billerCategory: this.billerCategory || '',
      firstFieldFormat: this.firstFieldFormat || '',
      secondFieldFormat: this.secondFieldFormat || '',
      imageFile: this.imageFile || null
    };
  }

  hasChanges(): boolean {
    const currentValues = this.getCurrentValues();
    const hasFormChanges = this.changeDetectionService.hasChanges(
      this.originalValues, 
      currentValues
    );
    
    return hasFormChanges;
  }

  isSubmitDisabled(): boolean {
    return !this.hasChanges();
  }

  async onSelectFile(event) {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const previousImageFile = this.imageFile ? { ...this.imageFile } : null;
      
      const imageFile = await this.imageValidatorService.processFileSelection(file, this.payBillsService.maxFileSize, this.payBillsService.maxImageDimension);
      
      if (imageFile) {
        this.imageFile = imageFile;
        this.payBillsForm.get('image')?.markAsTouched();
        this.payBillsForm.get('image')?.updateValueAndValidity();
      } else {
        if (previousImageFile) {
          this.imageFile = previousImageFile;
        } else {
          this.imageFile = null;
        }
        event.target.value = '';
      }
    }
  }

  print() {
    this.spinner.show();
    const current = new Date();
    const params = JSON.parse(localStorage.getItem('paybills_lists'));
    const fileName = current.toLocaleDateString() + '-paybills.xlsx';

    this.request.postBlob(Urls.exportPaybills, params).then((report) => {
      const blob = new Blob([report], { type: 'application/vnd.ms-excel' });
      const data = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = data;
      link.download = fileName;
      link.dispatchEvent(
        new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
      );

      setTimeout(() => {
        this.spinner.hide();
      }, 500);
    });
  }
}
